/**
 * Iter 67cn — Rental-info strip extracted from CarDetail.jsx.
 *
 * Renders the "min rental / max rental / driver age / deposit method /
 * pick-up payments" callout that sits below the mileage policy. Pulled out
 * verbatim — same testids, same Tailwind, same gating logic — so we can
 * trim the giant page render and reuse the strip on any future vendor
 * preview screen without duplicating the JSX.
 */
import { CalendarDays, UserCheck, Wallet, Banknote } from "lucide-react";
import PickupPaymentMethods from "@/components/car/PickupPaymentMethods";

const DEPOSIT_METHOD_LABELS = {
  cash: "Cash only",
  card: "Credit / debit card only",
  cash_or_card: "Cash or credit / debit card",
};

export default function CarRentalInfoStrip({ car }) {
  const minDays = Number(car.minimum_rental_days) || 0;
  const maxDays = Number(car.maximum_rental_days) || 0;
  const minAge = Number(car.minimum_driver_age) || 0;
  const hasDepositMethod = car.deposit_payment_method && Number(car.deposit) > 0;
  const pickupMethods = Array.isArray(car.pickup_payment_methods_resolved)
    ? car.pickup_payment_methods_resolved
    : [];
  // Match the original visibility predicate exactly so we don't change any
  // existing render behaviour.
  if (
    minDays <= 1 &&
    maxDays <= 0 &&
    minAge <= 0 &&
    !hasDepositMethod &&
    pickupMethods.length === 0
  ) {
    return null;
  }

  return (
    <div
      className="rounded-3xl border border-indigo-100 bg-gradient-to-br from-sky-50 via-white to-violet-50/50 p-5 my-6 space-y-2.5"
      data-testid="min-rental-info"
    >
      {minDays > 1 && (
        <div className="flex items-start gap-3 rounded-xl bg-white/60 border border-sky-100 px-3 py-2.5">
          <div className="h-8 w-8 shrink-0 rounded-xl bg-sky-50 ring-1 ring-sky-100 flex items-center justify-center">
            <CalendarDays className="h-4 w-4 text-sky-600" />
          </div>
          <div className="text-sm leading-snug">
            <span className="font-semibold text-neutral-900">Minimum rental:</span>{" "}
            <span className="text-neutral-700">{minDays} days</span>
          </div>
        </div>
      )}
      {maxDays > 0 && (
        <div className="flex items-start gap-3 rounded-xl bg-white/60 border border-rose-100 px-3 py-2.5">
          <div className="h-8 w-8 shrink-0 rounded-xl bg-rose-50 ring-1 ring-rose-100 flex items-center justify-center">
            <CalendarDays className="h-4 w-4 text-rose-600" />
          </div>
          <div className="text-sm leading-snug">
            <span className="font-semibold text-neutral-900">Maximum rental:</span>{" "}
            <span className="text-neutral-700">{maxDays} days</span>
            <span className="text-neutral-500"> — for longer stays, please message the host for a custom quote.</span>
          </div>
        </div>
      )}
      {minAge > 0 && (
        <div className="flex items-start gap-3 rounded-xl bg-white/60 border border-violet-100 px-3 py-2.5" data-testid="car-min-driver-age-info">
          <div className="h-8 w-8 shrink-0 rounded-xl bg-violet-50 ring-1 ring-violet-100 flex items-center justify-center">
            <UserCheck className="h-4 w-4 text-violet-600" />
          </div>
          <div className="text-sm leading-snug">
            <span className="font-semibold text-neutral-900">Minimum driver age:</span>{" "}
            <span className="text-neutral-700">{minAge}+ years</span>
            <span className="text-neutral-500"> — the host will verify your ID at pickup.</span>
          </div>
        </div>
      )}
      {hasDepositMethod && (
        <div className="flex items-start gap-3 rounded-xl bg-white/60 border border-amber-100 px-3 py-2.5" data-testid="car-deposit-method-info">
          <div className="h-8 w-8 shrink-0 rounded-xl bg-amber-50 ring-1 ring-amber-100 flex items-center justify-center">
            <Wallet className="h-4 w-4 text-amber-600" />
          </div>
          <div className="text-sm leading-snug">
            <span className="font-semibold text-neutral-900">Deposit payment:</span>{" "}
            <span className="text-neutral-700">
              {DEPOSIT_METHOD_LABELS[car.deposit_payment_method] || car.deposit_payment_method}
            </span>
            <span className="text-neutral-500"> — collected by the host at handover.</span>
          </div>
        </div>
      )}
      {pickupMethods.length > 0 && (
        <div className="flex items-start gap-3 rounded-xl bg-white/60 border border-emerald-100 px-3 py-2.5" data-testid="car-pickup-methods-info">
          <div className="h-8 w-8 shrink-0 rounded-xl bg-emerald-50 ring-1 ring-emerald-100 flex items-center justify-center">
            <Banknote className="h-4 w-4 text-emerald-600" />
          </div>
          <div className="text-sm leading-snug min-w-0 flex-1">
            <div className="mb-1.5">
              <span className="font-semibold text-neutral-900">Pay at pick-up:</span>
              <span className="text-neutral-500"> the host accepts these methods for the rental balance.</span>
            </div>
            <PickupPaymentMethods methods={pickupMethods} />
          </div>
        </div>
      )}
    </div>
  );
}
